import { useState } from 'react';
import { css } from '@emotion/react';
import { Virtuoso } from 'react-virtuoso';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import {
  NakedButton,
  PopoverDialog,
  SearchField,
  Text,
  useControlledField,
  usePopoverDialog,
} from '@frontend/design-system';
import { formatFieldLabel } from '../../../../../../helpers';
import { useGetInsurancePayers } from '../../../../../../hooks/api-hooks';
import type { InsuranceVerificationValue } from '../../../../../../types/form-field-state.types';
import { useFillingExperienceContext } from '../../../../provider/filling-experience.context';
import type { DynamicFormApi } from '../../hooks/use-app-form';
import {
  buildOptions,
  filterPayers,
  makeOptionKey,
  OTHER_OPTION_VALUE,
  parseOptionKey,
} from './insurance-verification-field.helpers';

const ITEM_HEIGHT = 38;
const MAX_VISIBLE_ITEMS = 10;

interface InsurerDropdownProps {
  form: DynamicFormApi;
  fieldKey: string;
  required: boolean;
}

export const InsurerDropdown = ({ form, fieldKey, required }: InsurerDropdownProps) => {
  const { locationId } = useFillingExperienceContext();
  const { payers } = useGetInsurancePayers(locationId);
  const { t } = useTranslation('digital-forms-experience');
  const [searchValue, setSearchValue] = useState('');

  const { getTriggerProps, getDialogProps, close } = usePopoverDialog<HTMLButtonElement>({
    placement: 'bottom',

    middlewareOptions: {
      size: {
        apply({ elements, rects }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
          });
        },
        padding: 8,
      },
    },
  });

  const searchFieldProps = useControlledField({
    type: 'text',
    value: searchValue,
    onChange: (val: string) => setSearchValue(val),
  });

  const dropdownValidator = ({ value }: { value: unknown }) => {
    const v = value as InsuranceVerificationValue | undefined;
    if (required && !v?.isCustomPayer && !v?.payerId) {
      return t('Payer Name is required');
    }
    return undefined;
  };

  return (
    <form.AppField
      name={fieldKey}
      validators={{
        onChange: dropdownValidator,
      }}
    >
      {(field) => {
        const fieldValue = field.state.value as InsuranceVerificationValue;
        const hasError = field.state.meta.isTouched && field.state.meta.errors?.length > 0;
        const errorMessage = hasError ? field.state.meta.errors.join(', ') : '';

        const isCustomPayer = !!fieldValue?.isCustomPayer;

        const selectedPayer = payers.find((p) => p.id === fieldValue?.payerId);
        const selectedPayerName = selectedPayer?.name ?? fieldValue?.payerName ?? '';

        const displayValue = isCustomPayer ? t('Other') : selectedPayerName;

        const filteredPayers = filterPayers(payers, searchValue);
        const allOptions = buildOptions(filteredPayers, searchValue, t('Other'));

        const selectedKey = isCustomPayer
          ? OTHER_OPTION_VALUE
          : makeOptionKey(fieldValue?.payerId ?? '', selectedPayerName);
        const handleSelect = (key: string) => {
          close();
          setSearchValue('');
          if (key === OTHER_OPTION_VALUE) {
            field.handleChange({ isCustomPayer: true, payerId: '', payerName: '' });
          } else {
            const { id, name } = parseOptionKey(key);
            field.handleChange({ isCustomPayer: false, payerId: id, payerName: name });
          }
          field.handleBlur();
        };

        const listHeight = Math.min(allOptions.length, MAX_VISIBLE_ITEMS) * ITEM_HEIGHT;

        return (
          <>
            <div>
              <NakedButton
                id={fieldKey}
                css={[triggerStyle, hasError && triggerErrorStyle]}
                {...getTriggerProps()}
                aria-invalid={hasError}
                onBlur={field.handleBlur}
              >
                <Text css={displayTextStyle(!!displayValue)}>{displayValue || t('Select Payer Name')}</Text>
                <Icon name='caret-down-small' css={chevronStyle} />
              </NakedButton>
              {hasError && (
                <Text size='small' css={errorMessageStyle}>
                  {errorMessage}
                </Text>
              )}
            </div>

            <PopoverDialog {...getDialogProps()} css={popoverStyle}>
              <SearchField
                {...searchFieldProps}
                name={`${fieldKey}-payer-search`}
                placeholder={t('Search payers...')}
                css={searchFieldStyle}
              />

              {allOptions.length === 0 ? (
                <div css={noResultsStyle}>
                  <Text>{t('No matching results found!')}</Text>
                </div>
              ) : (
                <Virtuoso
                  style={{ height: `${listHeight}px` }}
                  data={allOptions}
                  itemContent={(_i, option) => {
                    const key =
                      option.id === OTHER_OPTION_VALUE ? OTHER_OPTION_VALUE : makeOptionKey(option.id, option.name);
                    return (
                      <NakedButton
                        css={[optionStyle, key === selectedKey && activeOptionStyle]}
                        onClick={() => handleSelect(key)}
                      >
                        <Text>{option.name}</Text>
                      </NakedButton>
                    );
                  }}
                />
              )}
            </PopoverDialog>

            {isCustomPayer && (
              <>
                <form.AppField name={`${fieldKey}.payerId`}>
                  {(f) => (
                    <f.FormField label={t('Payer ID')} type='text' fieldKey={`${fieldKey}.payerId`} keepInlineLabel />
                  )}
                </form.AppField>

                <form.AppField
                  name={`${fieldKey}.payerName`}
                  validators={{
                    onSubmit: ({ value }) => {
                      if (required && !String(value ?? '').trim()) return t('Payer Name is required');
                      return undefined;
                    },
                  }}
                >
                  {(f) => (
                    <f.FormField
                      label={formatFieldLabel(t('Payer Name'), required)}
                      type='text'
                      fieldKey={`${fieldKey}.payerName`}
                      keepInlineLabel
                    />
                  )}
                </form.AppField>
              </>
            )}
          </>
        );
      }}
    </form.AppField>
  );
};

const triggerStyle = css`
  display: flex;
  align-items: center;
  padding: ${theme.spacing(1, 2)};
  border: 1px solid ${theme.colors.neutral30};
  border-radius: ${theme.borderRadius.small};
  cursor: pointer;
  text-align: left;
  width: 100%;
  gap: ${theme.spacing(1)};

  &:hover {
    border-color: ${theme.colors.primary50};
  }

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary50};
  }
`;

const triggerErrorStyle = css`
  border-color: ${theme.colors.critical40};

  p {
    color: ${theme.colors.text.critical};
  }

  &:hover {
    border-color: ${theme.colors.critical40};
  }

  &:focus {
    border-color: ${theme.colors.critical40};
  }
`;

const errorMessageStyle = css`
  display: block;
  margin-top: ${theme.spacing(0.5)};
  color: ${theme.colors.text.critical};
`;

const displayTextStyle = (hasValue: boolean) => css`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: ${hasValue ? theme.colors.text.default : theme.colors.text.subdued};
`;

const popoverStyle = css`
  padding: ${theme.spacing(1.5)};
  min-width: 280px;
`;

const searchFieldStyle = css`
  margin-bottom: ${theme.spacing(1)};
`;

const noResultsStyle = css`
  padding: ${theme.spacing(2)};
  text-align: center;
`;

const optionStyle = css`
  padding: ${theme.spacing(1, 1.5)};
  width: 100%;
  text-align: left;
  border-radius: ${theme.borderRadius.small};

  p {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  &:hover {
    background-color: ${theme.colors.neutral5};
  }
`;

const activeOptionStyle = css`
  background-color: ${theme.colors.primary5};
  color: ${theme.colors.primary50};
`;

const chevronStyle = css`
  margin-left: auto;
  flex-shrink: 0;
  color: ${theme.colors.neutral50};
`;
